/**
 * Off-menu lines, as the kitchen must read them.
 *
 * An off-menu line points at a hidden anchor dish called "Fuori menu" — a
 * bookkeeping row, not a plate. Showing that name is the one thing this screen
 * must never do: the cook would read "Fuori menu" and have no idea what to
 * cook. What the waiter actually wrote lives on the line itself, and it is the
 * title. "Fuori menu" survives only as a small tag, so the cook knows the plate
 * is not on the menu and that the note is the whole recipe.
 */

import type { KdsT } from "./i18n/settings";

/** The label shown as a tag beside the waiter's text. */
export const OFF_MENU_TAG = (t: KdsT): string => t("kds.offMenu.tag", "Fuori menu");

function clean(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/** True when this line was written at the table instead of picked from the menu. */
export function isOffMenuItem(item: any): boolean {
  return clean(item?.offMenuName).length > 0;
}

/**
 * Feature 270 — the separator between a dish and the reason it was re-priced.
 *
 * ⚠️ MIRROR of `REASON_SEPARATOR` in `@callcenterai/table-domain`
 * (`orders/item-display-name.ts`). This screen is built by Vite and does not
 * depend on that package — the grouping library is hand-mirrored here for the
 * same reason. If the separator changes there, change it here: a cook reading
 * "Gamberoni - porzione da 6" while the bill says "Gamberoni · porzione da 6"
 * is two names for one plate.
 */
const REASON_SEPARATOR = " · ";

/**
 * What to print as the line's title: the waiter's text when there is one, the
 * dish's name otherwise. `fallback` covers a line that carries neither.
 *
 * Feature 270 — when the till re-priced this line, the reason it typed comes
 * with the name. On this screen that is not decoration: the guest who is being
 * charged for six prawns has to be served six, and "porzione da 6" is the only
 * place the cook can read that. The bill, the pre-bill and the fiscal receipt
 * compose the same string, so paper and screen name the plate identically.
 */
export function itemDisplayName(item: any, fallback: string = "—"): string {
  const name = clean(item?.offMenuName) || clean(item?.dish?.name) || fallback;
  const reason = clean(item?.priceOverrideReason);
  return reason ? `${name}${REASON_SEPARATOR}${reason}` : name;
}
